/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import './App.css';

const API_BASE_URL = 'https://portfolio.edusoft.top/api/index.php/api';
const PROFILE_ID = 1;

// Global functions for the modal, to be called from child components
let openModalGlobal;
let closeModalGlobal;

// ฟังก์ชันสำหรับสร้าง URL ที่ถูกต้อง - ใช้ร่วมกันทั้ง app
const getFullUrl = (filePath) => {
    if (!filePath) return '';
    
    // ถ้าเป็น URL เต็มแล้ว
    if (filePath.startsWith('http')) {
        return filePath;
    }
    
    // ถ้าเริ่มด้วย uploads/ ให้เพิ่ม base URL
    if (filePath.startsWith('uploads/')) {
        return `${API_BASE_URL.replace('/api/index.php/api', '')}/${filePath}`;
    }
    
    // ถ้าเริ่มด้วย / ให้เพิ่ม base URL
    if (filePath.startsWith('/')) {
        return `${API_BASE_URL.replace('/api/index.php/api', '')}${filePath}`;
    }
    
    // กรณีอื่นๆ ให้เพิ่ม base URL และ uploads/
    return `${API_BASE_URL.replace('/api/index.php/api', '')}/uploads/${filePath}`;
};

// เพิ่ม function สำหรับตรวจสอบประเภทไฟล์
const getFileType = (fileName) => {
    if (!fileName) return 'unknown';
    const extension = fileName.toLowerCase().split('.').pop();
    
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const pdfTypes = ['pdf'];
    const officeTypes = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
    const videoTypes = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];
    
    if (imageTypes.includes(extension)) return 'image';
    if (pdfTypes.includes(extension)) return 'pdf';
    if (officeTypes.includes(extension)) return 'office';
    if (videoTypes.includes(extension)) return 'video';
    return 'other';
};

// --- New Component for a single Work Item ---
const WorkItem = ({ work, workCategories }) => {
    const hasImages = work.images && work.images.length > 0;
    const mainImage = hasImages ? work.images[0].image_path : null;
    const [currentMainImage, setCurrentMainImage] = useState(mainImage);

    return (
        <div className="card animate-in" key={work.id}>
            <div className="card-header">
                <h3 className="card-title">
                    <i className={workCategories[work.category] || 'fas fa-star'} /> {work.title}
                </h3>
            </div>
            <div className="card-body">
                {currentMainImage && (
                    <img 
                        src={getFullUrl(currentMainImage)} 
                        alt={work.title} 
                        className="work-image-main"
                        onError={(e) => {
                            console.log('Work image load error:', e.target.src);
                            e.target.style.display = 'none';
                        }}
                    />
                )}
                {work.images && work.images.length > 1 && (
                    <div className="work-image-gallery">
                        {work.images.map((img, index) => (
                            <div
                                className="work-thumbnail-wrapper"
                                key={index}
                                style={{ zIndex: index + 1 }}
                                onMouseEnter={() => setCurrentMainImage(img.image_path)}
                                onClick={() => setCurrentMainImage(img.image_path)}
                            >
                                <img 
                                    src={getFullUrl(img.image_path)} 
                                    alt={`${work.title} thumbnail ${index + 1}`} 
                                    className="work-thumbnail"
                                    onError={(e) => {
                                        console.log('Work thumbnail load error:', e.target.src);
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
                <p><strong>ประเภท:</strong> {work.category}</p>
                <p><strong>ระดับ:</strong> {work.level || 'ไม่มีข้อมูล'}</p>
                <p><strong>หน่วยงาน:</strong> {work.organization || 'ไม่มีข้อมูล'}</p>
                <p><strong>วันที่:</strong> {new Date(work.achievement_date).toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                {work.description && <p><strong>คำอธิบาย:</strong> {work.description}</p>}
            </div>
        </div>
    );
};

// --- New Component for a single Evaluation Indicator ---
const EvaluationIndicator = ({ indicator }) => {
    const hasSummary = indicator.summary && indicator.summary.trim() !== '';
    const hasEvidence = indicator.evidences && indicator.evidences.length > 0;
    
    // แยกประเภท evidence
    const imageEvidence = hasEvidence ? indicator.evidences.filter(e => e.evidence_type === 'รูปภาพ') : [];
    const fileEvidence = hasEvidence ? indicator.evidences.filter(e => e.evidence_type !== 'รูปภาพ') : [];

    const mainImage = imageEvidence.length > 0 ? imageEvidence[0].file_path : null;
    const [currentMainImage, setCurrentMainImage] = useState(mainImage);

    return (
        <div className="card animate-in">
            <div className="card-header">
                <h3 className="card-title">
                    ตัวชี้วัดที่ {indicator.indicator_code} - {indicator.indicator_name}
                </h3>
            </div>
            <div className="card-body">
                <p style={{ fontSize: '0.95rem', color: '#64748b' }}>{indicator.indicator_description || 'ไม่มีคำอธิบาย'}</p>
                {hasSummary && (
                    <>
                        <h4>สรุปผลการปฏิบัติงาน</h4>
                        <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', marginTop: '0.5rem', borderLeft: '3px solid var(--accent-color)' }}>
                            <p style={{ fontSize: '0.95rem' }}>{indicator.summary}</p>
                        </div>
                    </>
                )}
                
                {/* แสดงรูปภาพ */}
                {currentMainImage && (
                    <img 
                        src={getFullUrl(currentMainImage)} 
                        alt="Main evidence" 
                        className="eval-image-main"
                        onError={(e) => {
                            console.log('Image load error:', e.target.src);
                            e.target.style.display = 'none';
                        }}
                    />
                )}
                
                {/* Gallery รูปภาพเพิ่มเติม */}
                {imageEvidence.length > 1 && (
                    <div className="eval-image-gallery">
                        {imageEvidence.map((img, index) => (
                            <div
                                className="eval-thumbnail-wrapper"
                                key={index}
                                style={{ zIndex: index + 1 }}
                                onMouseEnter={() => setCurrentMainImage(img.file_path)}
                                onClick={() => setCurrentMainImage(img.file_path)}
                            >
                                <img 
                                    src={getFullUrl(img.file_path)} 
                                    alt="Thumbnail" 
                                    className="eval-thumbnail"
                                    onError={(e) => {
                                        console.log('Thumbnail load error:', e.target.src);
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
                
                {/* แสดงไฟล์หลักฐาน */}
                {fileEvidence.length > 0 && (
                    <>
                        <h4>หลักฐานประกอบ</h4>
                        <ul className="evidence-list">
                            {fileEvidence.map((file, i) => (
                                <li key={i}>
                                    <a 
                                        href="#" 
                                        onClick={(e) => { 
                                            e.preventDefault(); 
                                            const fullUrl = getFullUrl(file.file_path);
                                            console.log('Opening file:', fullUrl);
                                            console.log('File info:', file);
                                            // ส่งข้อมูลไฟล์ไปยัง modal
                                            openModalGlobal(fullUrl, file.title || file.file_name, file.file_name); 
                                        }}
                                        className="evidence-link"
                                    >
                                        <i className="fas fa-file-alt indicator-icon" />
                                        <span>{file.file_name || file.title}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                
                {/* แสดงข้อความเมื่อไม่มีข้อมูล */}
                {!hasEvidence && !hasSummary && (
                    <p style={{ textAlign: 'center', padding: '2rem' }}>
                        ไม่มีข้อมูลการรายงานสำหรับตัวชี้วัดนี้
                    </p>
                )}
            </div>
        </div>
    );
};

function Home() {
    const [profile, setProfile] = useState(null);
    const [works, setWorks] = useState(null);
    const [evaluations, setEvaluations] = useState(null);
    const [challenge, setChallenge] = useState(null);
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('profile');
    const [modalVisible, setModalVisible] = useState(false);
    const [modalUrl, setModalUrl] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [modalFileType, setModalFileType] = useState('');
    const sectionsRef = useRef([]);

    openModalGlobal = (url, title, fileName = '') => {
        let fileUrl;
        
        // สร้าง URL ที่ถูกต้อง
        if (url.startsWith('http')) {
            fileUrl = url;
        } else if (url.startsWith('/')) {
            fileUrl = `${API_BASE_URL.replace('/api/index.php/api', '')}${url}`;
        } else if (url.startsWith('uploads/')) {
            fileUrl = `${API_BASE_URL.replace('/api/index.php/api', '')}/${url}`;
        } else {
            fileUrl = url;
        }
        
        console.log('Opening modal with URL:', fileUrl);
        
        const fileType = getFileType(fileName || title);
        console.log('File type:', fileType);
        
        setModalUrl(fileUrl);
        setModalTitle(title || fileName);
        setModalFileType(fileType);
        setModalVisible(true);
        document.body.style.overflow = 'hidden';
    };

    closeModalGlobal = () => {
        setModalVisible(false);
        setModalUrl('');
        setModalTitle('');
        setModalFileType('');
        document.body.style.overflow = 'auto';
    };

    const fetchData = async (endpoint, signal) => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, { signal });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data.data;
    };

    useEffect(() => {
        const fetchYearsAndData = async () => {
            setLoading(true);
            const controller = new AbortController();
            const signal = controller.signal;

            try {
                const yearsData = await fetchData('/academic-years', signal);
                setAcademicYears(yearsData);

                const latestYear = yearsData.length > 0 ? yearsData[0].year_name : null;
                setSelectedYear(latestYear);

                await fetchAllDataForYear(latestYear, signal);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Failed to load initial data:', error);
                    setLoading(false);
                }
            }
        };
        fetchYearsAndData();
    }, []);

    const fetchAllDataForYear = async (year, signal) => {
        setLoading(true);
        try {
            const [profileData, worksData, evaluationsData, challengesData] = await Promise.all([
                fetchData(`/profile/${PROFILE_ID}`, signal),
                fetchData(`/works/${PROFILE_ID}`, signal),
                fetchData(`/evaluations/${PROFILE_ID}/${year || ''}`, signal),
                fetchData(`/challenges/${PROFILE_ID}/${year || ''}`, signal),
            ]);
            setProfile(profileData);
            setWorks(worksData.by_category);
            setEvaluations(evaluationsData.by_domain);
            setChallenge(challengesData.length > 0 ? challengesData[0] : null);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Failed to load data for year:', error);
                setProfile(null);
                setWorks(null);
                setEvaluations(null);
                setChallenge(null);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = (event) => {
        const newYear = event.target.value;
        setSelectedYear(newYear);
        fetchAllDataForYear(newYear);
    };

    // Intersection Observer for Scroll Spy
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        const targetId = entry.target.id;
                        setActiveSection(targetId);
                    }
                });
            },
            {
                root: null,
                rootMargin: '0px 0px -50% 0px',
                threshold: 0.5,
            }
        );

        sectionsRef.current.forEach((section) => {
            if (section) {
                observer.observe(section);
            }
        });

        return () => {
            sectionsRef.current.forEach((section) => {
                if (section) {
                    observer.unobserve(section);
                }
            });
        };
    }, [loading]);

    // Function to handle smooth scroll on link click
    const handleNavLinkClick = (e, sectionId) => {
        e.preventDefault();
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setActiveSection(sectionId);
        }
        // เพิ่มโค้ด 2 บรรทัดนี้
        const sidebar = document.getElementById('sidebar');
        const toggleButton = document.querySelector('.sidebar-toggle');
        sidebar.classList.remove('active');
        toggleButton.classList.remove('active');
    };

    // --- Components for each section ---
    const ProfileSection = () => {
        if (!profile) return <p>ไม่มีข้อมูลส่วนตัว</p>;
        
        // แก้ไขการแสดงรูปโปรไฟล์
        const profileImage = profile.profile_image && profile.profile_image.trim() !== ''
            ? <img 
                src={getFullUrl(profile.profile_image)} 
                alt="Profile" 
                style={{ width: '200px', height: '200px', borderRadius: '50%', objectFit: 'cover' }}
                onError={(e) => {
                    console.log('Profile image load error:', e.target.src);
                    // แสดง icon แทนเมื่อโหลดรูปไม่ได้
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                }}
              />
            : null;

        return (
            <div className="card animate-in">
                <div className="card-header">
                    <h2 className="card-title"><i className="fas fa-user" /> ข้อมูลส่วนตัว</h2>
                </div>
                <div className="profile-grid">
                    <div className="profile-image-section">
                        <div className="profile-image">
                            {profileImage}
                            <i className="fas fa-user" style={{ display: profileImage ? 'none' : 'flex' }} />
                        </div>
                        <h3 className="profile-name">{`${profile.title_name || ''}${profile.first_name || ''} ${profile.last_name || ''}`}</h3>
                        <p className="profile-position">{`${profile.position || ''} ${profile.department || ''}`}</p>
                        <div className="contact-info">
                            <div className="contact-item"><i className="fas fa-envelope" /><span>{profile.email || 'ไม่มีข้อมูล'}</span></div>
                            <div className="contact-item"><i className="fas fa-phone" /><span>{profile.phone || 'ไม่มีข้อมูล'}</span></div>
                            <div className="contact-item"><i className="fas fa-school" /><span>{profile.school_name || 'ไม่มีข้อมูล'}</span></div>
                        </div>
                    </div>
                    <div className="info-section">
                        <div className="info-card">
                            <h3><i className="fas fa-graduation-cap" /> ประวัติการศึกษา</h3>
                            <ul>
                                {profile.educations?.length > 0 ? profile.educations.map((edu, i) => (
                                    <li key={i}><strong>{edu.degree_level || ''}</strong> {edu.major || ''} {edu.university || ''} (พ.ศ. {parseInt(edu.graduation_year) + 543 || ''})</li>
                                )) : <li>ไม่มีข้อมูลประวัติการศึกษา</li>}
                            </ul>
                        </div>
                        <div className="info-card">
                            <h3><i className="fas fa-briefcase" /> ประสบการณ์การทำงาน</h3>
                            <ul>
                                {profile.work_experiences?.length > 0 ? profile.work_experiences.map((work, i) => (
                                    <li key={i}><strong>{parseInt(work.start_year) + 543 || ''}-{work.end_year ? parseInt(work.end_year) + 543 : ''}</strong>: {work.position || ''} {work.organization || ''}</li>
                                )) : <li>ไม่มีข้อมูลประสบการณ์การทำงาน</li>}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const WorksSection = () => {
        const workCategories = {
            'รางวัล': 'fas fa-medal', 'งานวิจัย': 'fas fa-book', 'วิทยากร': 'fas fa-chalkboard-teacher',
            'โครงการ': 'fas fa-project-diagram', 'อื่นๆ': 'fas fa-star'
        };

        if (!works || Object.values(works).flat().length === 0) return <p style={{ textAlign: 'center' }}>ไม่มีข้อมูลผลงาน</p>;

        return (
            <div className="works-container single-column-grid">
                {Object.keys(works).map(category =>
                    works[category].map(work => (
                        <WorkItem key={work.id} work={work} workCategories={workCategories} />
                    ))
                )}
            </div>
        );
    };

    const EvaluationOverviewSection = () => {
        if (!evaluations) return <p style={{ textAlign: 'center' }}>ไม่มีข้อมูลการประเมินในขณะนี้</p>;
        return (
            <div className="evaluation-grid">
                {evaluations.map(domain => (
                    <div className="evaluation-card" key={domain.domain_number} onClick={(e) => handleNavLinkClick(e, `evaluation-${domain.domain_number}`)}>
                        <div className="evaluation-header">
                            <h3 className="evaluation-title">ด้านที่ {domain.domain_number}: {domain.domain_name}</h3>
                        </div>
                        <div className="evaluation-content">
                            <p>{domain.domain_description}</p>
                            <ul className="indicator-list">
                                {(domain.indicators || []).slice(0, 3).map((indicator, i) => (
                                    <li className="indicator-item" key={i}><i className="fas fa-check-circle indicator-icon" /><span>{indicator.indicator_name}</span></li>
                                ))}
                            </ul>
                            <div style={{ textAlign: 'center', marginTop: '1rem' }}><small style={{ color: '#64748b' }}>คลิกเพื่อดูรายละเอียด</small></div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const EvaluationDomainSection = ({ domainNumber }) => {
        if (!evaluations) return <p style={{ textAlign: 'center' }}>ไม่มีข้อมูลการประเมินในขณะนี้</p>;

        const domain = evaluations.find(d => d.domain_number === domainNumber);
        if (!domain || !domain.indicators || domain.indicators.length === 0) {
            return <p style={{ textAlign: 'center' }}>ไม่มีข้อมูลการประเมินสำหรับด้านนี้</p>;
        }
        
        // Use a Map to filter for unique indicators, keeping the last one if duplicates exist.
        const uniqueIndicatorsMap = new Map();
        domain.indicators.forEach(indicator => {
            uniqueIndicatorsMap.set(indicator.indicator_code, indicator);
        });
        const uniqueIndicators = Array.from(uniqueIndicatorsMap.values());
        
        // Sort the unique indicators by their indicator code as numbers
        const sortedIndicators = [...uniqueIndicators].sort((a, b) => {
            const aParts = a.indicator_code.split('.').map(Number);
            const bParts = b.indicator_code.split('.').map(Number);

            for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
                const diff = (aParts[i] || 0) - (bParts[i] || 0);
                if (diff !== 0) {
                    return diff;
                }
            }
            return 0;
        });

        return (
            <div className="single-column-grid">
                {sortedIndicators.map(indicator => (
                    <EvaluationIndicator key={indicator.id} indicator={indicator} />
                ))}
            </div>
        );
    };

    const ChallengeSection = () => {
        if (!challenge) return <p style={{ textAlign: 'center' }}>ไม่มีข้อมูลประเด็นท้าทายสำหรับปีที่เลือก</p>;

        const quantitativeResults = challenge.results?.quantitative ? Object.values(challenge.results.quantitative) : [];
        const qualitativeResults = challenge.results?.qualitative ? Object.values(challenge.results.qualitative) : [];
        const challengeTools = challenge.tools || [];
        const challengeSteps = challenge.steps || [];

        return (
            <div className="challenge-section animate-in">
                <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                    <h2 style={{ color: 'var(--primary-color)', fontSize: '2rem', marginBottom: '1rem' }}>
                        <i className="fas fa-lightbulb" /> "{challenge.title || 'ไม่มีชื่อประเด็นท้าทาย'}"
                    </h2>
                    <div style={{ background: '#fef3c7', padding: '2rem', borderRadius: '15px', margin: '2rem 0' }}>
                        <h4 style={{ color: 'var(--text-color)', marginBottom: '1rem' }}>ประเด็นท้าทาย</h4>
                        <p style={{ color: 'var(--text-color)' }}>{challenge.description || 'ไม่มีคำอธิบาย'}</p>
                    </div>
                </div>
                <div className="challenge-steps">
                    {challengeSteps.map((step, i) => (
                        <div className="step-card" key={i}>
                            <div className="step-number">{step.step_number || ''}</div>
                            <h4 className="step-title">{step.step_title || ''}</h4>
                            <p>{step.step_description || ''}</p>
                        </div>
                    ))}
                </div>
                <h3 style={{ color: 'var(--primary-color)', margin: '3rem 0 2rem', textAlign: 'center', fontSize: '1.8rem' }}><i className="fas fa-bullseye" /> ผลลัพธ์ที่คาดหวัง</h3>
                <div className="results-grid">
                    <div className="result-card quantitative">
                        <h4 style={{ color: 'var(--primary-color)', marginBottom: '1rem' }}><i className="fas fa-chart-bar" /> ผลลัพธ์เชิงปริมาณ</h4>
                        <ul style={{ textAlign: 'left' }}>
                            {quantitativeResults.map((result, i) => <li key={i}>{result.description || ''}</li>)}
                        </ul>
                    </div>
                    <div className="result-card qualitative">
                        <h4 style={{ color: 'var(--accent-color)', marginBottom: '1rem' }}><i className="fas fa-heart" /> ผลลัพธ์เชิงคุณภาพ</h4>
                        <ul style={{ textAlign: 'left' }}>
                            {qualitativeResults.map((result, i) => <li key={i}>{result.description || ''}</li>)}
                        </ul>
                    </div>
                </div>
                <div style={{ background: '#e0f2fe', padding: '2rem', borderRadius: '15px', marginTop: '3rem' }}>
                    <h4 style={{ color: 'var(--text-color)', marginBottom: '1rem', textAlign: 'center' }}><i className="fas fa-tools" /> เครื่องมือและวิธีการ</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginTop: '2rem' }}>
                        {challengeTools.map((tool, i) => (
                            <div style={{ textAlign: 'center' }} key={i}>
                                <i className="fas fa-tools" style={{ fontSize: '2rem', color: 'var(--primary-color)', marginBottom: '1rem' }} />
                                <h5>{tool.tool_name || ''}</h5>
                                <p style={{ fontSize: '0.9rem' }}>{tool.description || ''}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                    <div style={{ background: 'white', border: '2px dashed var(--primary-color)', borderRadius: '15px', padding: '3rem' }}>
                        <i className="fas fa-file-pdf" style={{ fontSize: '4rem', color: '#ef4444', marginBottom: '1rem' }} />
                        <h4 style={{ marginBottom: '1rem' }}>เอกสารโครงการฉบับสมบูรณ์</h4>
                        <p style={{ color: '#64748b', marginBottom: '2rem' }}>รายละเอียดการดำเนินงาน แนวทาง และผลลัพธ์ที่คาดหวัง</p>
                        <a href={challenge.document_url || '#'} target="_blank" rel="noopener noreferrer" style={{ background: 'var(--primary-color)', color: 'white', border: 'none', padding: '1rem 2rem', borderRadius: '25px', fontSize: '1rem', cursor: 'pointer', transition: 'all 0.3s ease', textDecoration: 'none' }}>
                            <i className="fas fa-download" /> ดาวน์โหลดเอกสาร PDF
                        </a>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="App">
          <button className="sidebar-toggle" onClick={() => {
              const sidebar = document.getElementById('sidebar');
              const toggleButton = document.querySelector('.sidebar-toggle');
              sidebar.classList.toggle('active');
              toggleButton.classList.toggle('active');
          }}>
              <i className="fas fa-bars" />
          </button>

            <aside className="sidebar" id="sidebar">
                <div className="sidebar-header">
                    <div className="sidebar-logo-icon">
                        <img 
                            src={getFullUrl("logo/logo.png")}
                            alt="Logo" 
                            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                            onError={(e) => {
                                console.log('Logo load error:', e.target.src);
                                // แสดง default icon หากโหลดโลโก้ไม่ได้
                                e.target.style.display = 'none';
                                e.target.parentNode.style.background = 'var(--active-gradient)';
                                e.target.parentNode.innerHTML = '<i class="fas fa-graduation-cap" style="font-size: 1.5rem; color: white;"></i>';
                            }}
                        />
                    </div>
                    <div className="sidebar-logo-text">
                        <div className="sidebar-logo">VPA Portfolio</div>
                        <div className="sidebar-subtitle">ระบบบันทึกผลการปฏิบัติงาน</div>
                    </div>
                </div>
                <nav className="sidebar-nav">
                    <div className="nav-section">
                        <div className="nav-item">
                            <a href="#profile" className={`nav-link ${activeSection === 'profile' ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, 'profile')}><i className="fas fa-user" /><span className="nav-text">ข้อมูลส่วนตัว</span></a>
                        </div>
                        <div className="nav-item">
                            <a href="#works" className={`nav-link ${activeSection === 'works' ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, 'works')}><i className="fas fa-briefcase" /><span className="nav-text">ผลงาน</span></a>
                        </div>
                    </div>
                    <div className="nav-section">
                        <div className="nav-section-title">การประเมิน</div>
                        <div className="nav-item">
                            <a href="#evaluation-overview" className={`nav-link ${activeSection === 'evaluation-overview' ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, 'evaluation-overview')}><i className="fas fa-chart-line" /><span className="nav-text">ภาพรวมการประเมิน</span></a>
                        </div>
                        <div className="sub-nav">
                            <div className="nav-item">
                                <a href="#evaluation-1" className={`nav-link ${activeSection === 'evaluation-1' ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, 'evaluation-1')}><i className="fas fa-chalkboard-teacher" /><span className="nav-text">ด้านการจัดการเรียนรู้</span></a>
                            </div>
                            <div className="nav-item">
                                <a href="#evaluation-2" className={`nav-link ${activeSection === 'evaluation-2' ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, 'evaluation-2')}><i className="fas fa-hands-helping" /><span className="nav-text">ด้านการส่งเสริมและสนับสนุน</span></a>
                            </div>
                            <div className="nav-item">
                                <a href="#evaluation-3" className={`nav-link ${activeSection === 'evaluation-3' ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, 'evaluation-3')}><i className="fas fa-user-graduate" /><span className="nav-text">ด้านการพัฒนาตนเองและวิชาชีพ</span></a>
                            </div>
                        </div>
                    </div>
                    <div className="nav-section">
                        <div className="nav-item">
                            <a href="#challenge" className={`nav-link ${activeSection === 'challenge' ? 'active' : ''}`} onClick={(e) => handleNavLinkClick(e, 'challenge')}><i className="fas fa-lightbulb" /><span className="nav-text">ประเด็นท้าทาย</span></a>
                        </div>
                    </div>
                </nav>
                <div className="sidebar-footer">
                    <div className="footer-content">
                        <a href="https://portfolio.krutof.work/edit/" className="admin-icon"><i className="fas fa-cog" /></a>
                        <span className="powered-by">by EduSoft.top</span>
                    </div>
                </div>
            </aside>

            <main className="main-content">
                {loading && <p style={{ textAlign: 'center', fontSize: '1.5rem', color: '#64748b', marginTop: '5rem' }}>กำลังโหลดข้อมูล...</p>}
                {!loading && (
                    <>
                        <section id="profile" className="content-section" ref={(el) => (sectionsRef.current[0] = el)}>
                            <div className="section">
                                <div className="year-selector-container">
                                    <div className="year-selector">
                                        <label htmlFor="year-select">ปีการศึกษา:</label>
                                        <select id="year-select" value={selectedYear || ''} onChange={handleYearChange}>
                                            {academicYears.length > 0 ? (
                                                academicYears.map(year => (
                                                    <option key={year.year_name} value={year.year_name}>ปี {year.year_name}</option>
                                                ))
                                            ) : (
                                                <option value="">ไม่มีข้อมูล</option>
                                            )}
                                        </select>
                                    </div>
                                </div>
                                <ProfileSection />
                            </div>
                        </section>
                        <section id="works" className="content-section" ref={(el) => (sectionsRef.current[1] = el)}>
                            <div className="section">
                                <div className="card">
                                    <div className="card-header"><h2 className="card-title"><i className="fas fa-trophy" />ผลงาน</h2></div>
                                    <WorksSection />
                                </div>
                            </div>
                        </section>
                        <section id="evaluation-overview" className="content-section" ref={(el) => (sectionsRef.current[2] = el)}>
                            <div className="section">
                                <div className="card">
                                    <div className="card-header"><h2 className="card-title"><i className="fas fa-chart-line" />ภาพรวมการประเมิน</h2></div>
                                    <p>การประเมินตามภาระงานและพฤติกรรมการปฏิบัติงานจริงของครูในแต่ละด้าน แบ่งเป็น 3 ด้านหลัก</p>
                                </div>
                                <EvaluationOverviewSection />
                            </div>
                        </section>
                        <section id="evaluation-1" className="content-section" ref={(el) => (sectionsRef.current[3] = el)}>
                            <div className="section">
                                <div className="card">
                                    <div className="card-header"><h2 className="card-title"><i className="fas fa-chalkboard-teacher" />ด้านการจัดการเรียนรู้</h2></div>
                                    <EvaluationDomainSection domainNumber={1} />
                                </div>
                            </div>
                        </section>
                        <section id="evaluation-2" className="content-section" ref={(el) => (sectionsRef.current[4] = el)}>
                            <div className="section">
                                <div className="card">
                                    <div className="card-header"><h2 className="card-title"><i className="fas fa-hands-helping" />ด้านการส่งเสริมและสนับสนุน</h2></div>
                                    <EvaluationDomainSection domainNumber={2} />
                                </div>
                            </div>
                        </section>
                        <section id="evaluation-3" className="content-section" ref={(el) => (sectionsRef.current[5] = el)}>
                            <div className="section">
                                <div className="card">
                                    <div className="card-header"><h2 className="card-title"><i className="fas fa-user-graduate" />ด้านการพัฒนาตนเองและวิชาชีพ</h2></div>
                                    <EvaluationDomainSection domainNumber={3} />
                                </div>
                            </div>
                        </section>
                        <section id="challenge" className="content-section" ref={(el) => (sectionsRef.current[6] = el)}>
                            <div className="section">
                                <ChallengeSection />
                            </div>
                        </section>
                    </>
                )}
            </main>

            {/* Enhanced Modal */}
            {modalVisible && (
                <div className="modal" style={{ display: 'flex' }} onClick={closeModalGlobal}>
                    <div className="modal-content-enhanced" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-enhanced">
                            <h2>{modalTitle}</h2>
                            <div className="modal-actions">
                                <a 
                                    href={modalUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="modal-action-btn"
                                    title="เปิดในแท็บใหม่"
                                >
                                    <i className="fas fa-external-link-alt"></i>
                                </a>
                                <a 
                                    href={modalUrl} 
                                    download 
                                    className="modal-action-btn"
                                    title="ดาวน์โหลดไฟล์"
                                >
                                    <i className="fas fa-download"></i>
                                </a>
                                <button className="modal-close-btn" onClick={closeModalGlobal} title="ปิด">
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                        <div className="modal-body-enhanced">
                            {modalFileType === 'pdf' && (
                                <iframe 
                                    src={modalUrl} 
                                    className="modal-iframe" 
                                    title={modalTitle}
                                    onError={(e) => {
                                        console.log('PDF iframe error:', e);
                                        e.target.style.display = 'none';
                                        const fallback = e.target.parentNode.querySelector('.modal-fallback');
                                        if (fallback) fallback.style.display = 'flex';
                                    }}
                                />
                            )}
                            
                            {modalFileType === 'image' && (
                                <div className="modal-image-container">
                                    <img 
                                        src={modalUrl} 
                                        alt={modalTitle}
                                        className="modal-image"
                                        onError={(e) => {
                                            console.log('Image error:', e);
                                            e.target.style.display = 'none';
                                            const fallback = e.target.parentNode.parentNode.querySelector('.modal-fallback');
                                            if (fallback) fallback.style.display = 'flex';
                                        }}
                                    />
                                </div>
                            )}
                            
                            {modalFileType === 'video' && (
                                <div className="modal-video-container">
                                    <video 
                                        controls 
                                        className="modal-video"
                                        onError={(e) => {
                                            console.log('Video error:', e);
                                            e.target.style.display = 'none';
                                            const fallback = e.target.parentNode.parentNode.querySelector('.modal-fallback');
                                            if (fallback) fallback.style.display = 'flex';
                                        }}
                                    >
                                        <source src={modalUrl} />
                                        เบราว์เซอร์ของคุณไม่รองรับการเล่นวิดีโอ
                                    </video>
                                </div>
                            )}
                            
                            {(modalFileType === 'office' || modalFileType === 'other') && (
                                <iframe 
                                    src={`https://docs.google.com/gview?url=${encodeURIComponent(modalUrl)}&embedded=true`}
                                    className="modal-iframe" 
                                    title={modalTitle}
                                    onError={(e) => {
                                        console.log('Google Docs viewer error:', e);
                                        e.target.style.display = 'none';
                                        const fallback = e.target.parentNode.querySelector('.modal-fallback');
                                        if (fallback) fallback.style.display = 'flex';
                                    }}
                                />
                            )}
                            
                            {/* Fallback สำหรับกรณีที่โหลดไม่ได้ */}
                            <div className="modal-fallback" style={{ display: 'none' }}>
                                <div className="fallback-content">
                                    <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#f59e0b', marginBottom: '1rem' }}></i>
                                    <h3>ไม่สามารถแสดงไฟล์ได้</h3>
                                    <p>ไฟล์อาจเสียหายหรือไม่รองรับการแสดงผลในเบราว์เซอร์</p>
                                    <div className="fallback-actions">
                                        <a 
                                            href={modalUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="fallback-button"
                                        >
                                            <i className="fas fa-external-link-alt"></i> เปิดในแท็บใหม่
                                        </a>
                                        <a 
                                            href={modalUrl} 
                                            download 
                                            className="fallback-button"
                                        >
                                            <i className="fas fa-download"></i> ดาวน์โหลด
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppRouter;